/**
 * Admin Agents Page
 *
 * Snapshot of all agents in the system.
 * CRUD form with visual lead preferences editor (no JSON).
 * Detail drawer for quick inspection.
 * Assign AM dialog for client-to-AM assignment.
 */

import { useMemo, useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useMockAgents, MockAgent } from "@/contexts/MockAgentsContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { Eye, Building2, MapPin, TrendingUp, Target, Save, Trash2, Package, ChevronDown, Search, Headphones, Monitor, CreditCard, BarChart3 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useContracts, type Contract } from "@/contexts/ContractsContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// All available lead types for the visual editor
const ALL_LEAD_TYPES = [
  { id: "life_fex", name: "Final Expense (FEX)", vertical: "life_insurance" },
  { id: "life_iul", name: "Indexed Universal Life (IUL)", vertical: "life_insurance" },
  { id: "life_mp", name: "Mortgage Protection (MP)", vertical: "life_insurance" },
  { id: "mortgage_purchase", name: "Purchase", vertical: "mortgage" },
  { id: "mortgage_refi", name: "Refinance", vertical: "mortgage" },
  { id: "mortgage_heloc", name: "HELOC", vertical: "mortgage" },
  { id: "mortgage_reverse", name: "Reverse Mortgage", vertical: "mortgage" },
];

interface LeadPrefState {
  leadTypeId: string;
  leadTypeName: string;
  isActive: boolean;
  bidValue: number;
  dailyCap: number;
  monthlyCap: number;
}

interface AMOption {
  id: string;
  full_name: string;
  max_clients: number;
  active_client_count: number;
}

function buildDefaultPrefs(vertical: string): LeadPrefState[] {
  return ALL_LEAD_TYPES
    .filter((lt) => lt.vertical === vertical)
    .map((lt) => ({
      leadTypeId: lt.id,
      leadTypeName: lt.name,
      isActive: false,
      bidValue: 35,
      dailyCap: 5,
      monthlyCap: 100,
    }));
}

function parsePrefsFromAgent(agent: MockAgent): LeadPrefState[] {
  const existing = agent.leadPreferences || [];
  const verticalTypes = ALL_LEAD_TYPES.filter((lt) => lt.vertical === agent.vertical);

  return verticalTypes.map((lt) => {
    const found = existing.find((p) => p.leadTypeId === lt.id);
    if (found) {
      return {
        leadTypeId: lt.id,
        leadTypeName: lt.name,
        isActive: found.isActive ?? false,
        bidValue: found.bidValue ?? 35,
        dailyCap: found.dailyCap ?? 5,
        monthlyCap: found.monthlyCap ?? 100,
      };
    }
    return {
      leadTypeId: lt.id,
      leadTypeName: lt.name,
      isActive: false,
      bidValue: 35,
      dailyCap: 5,
      monthlyCap: 100,
    };
  });
}

// Aggregate billing status for an agent across all their contracts
interface AgentBillingSummary {
  activeContracts: number;
  pausedContracts: number;
  cancellingContracts: number;
  totalDelivered: number;
  totalCommitted: number;
  billingLabel: string;
  billingColor: string;
}

function computeAgentBilling(agentId: string, contracts: Contract[]): AgentBillingSummary {
  const agentContracts = contracts.filter(c => c.agentId === agentId);
  const active = agentContracts.filter(c => c.status === "active" || c.status === "at_risk");
  const paused = agentContracts.filter(c => c.status === "paused");
  const cancelling = agentContracts.filter(c => c.cancelAtPeriodEnd && c.status !== "cancelled");

  const totalDelivered = active.reduce((sum, c) => sum + c.leadsDelivered, 0)
    + paused.reduce((sum, c) => sum + c.leadsDelivered, 0);
  const totalCommitted = active.reduce((sum, c) => sum + c.totalLeadsCommitted, 0)
    + paused.reduce((sum, c) => sum + c.totalLeadsCommitted, 0);

  let billingLabel = "No Contract";
  let billingColor = "bg-gray-100 text-gray-600 border-gray-200";

  if (paused.length > 0 && active.length === 0) {
    billingLabel = "Paused";
    billingColor = "bg-amber-100 text-amber-700 border-amber-200";
  } else if (cancelling.length > 0) {
    billingLabel = "Cancelling";
    billingColor = "bg-red-100 text-red-700 border-red-200";
  } else if (active.length > 0) {
    billingLabel = "Active";
    billingColor = "bg-green-100 text-green-700 border-green-200";
  } else if (agentContracts.length > 0) {
    // Has contracts but all fulfilled/expired/cancelled
    billingLabel = "Inactive";
    billingColor = "bg-gray-100 text-gray-600 border-gray-200";
  }

  return {
    activeContracts: active.length,
    pausedContracts: paused.length,
    cancellingContracts: cancelling.length,
    totalDelivered,
    totalCommitted,
    billingLabel,
    billingColor,
  };
}

export default function AdminAgents() {
  const { clientAgents, actions } = useMockAgents();
  const { state: { contracts } } = useContracts();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState<MockAgent | null>(null);
  const [formAgentId, setFormAgentId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formCompany, setFormCompany] = useState("");
  const [formVertical, setFormVertical] = useState<MockAgent["vertical"]>("life_insurance");
  const [formTier, setFormTier] = useState<MockAgent["tier"]>("Gold");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formLicensedStates, setFormLicensedStates] = useState("");
  const [formLeadPrefs, setFormLeadPrefs] = useState<LeadPrefState[]>(buildDefaultPrefs("life_insurance"));
  const [formError, setFormError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");

  // Conversion funnel data for selected agent
  const [funnelData, setFunnelData] = useState<Record<string, number>>({});
  const [funnelLoading, setFunnelLoading] = useState(false);

  // Fetch funnel data when agent drawer opens
  useEffect(() => {
    if (!selectedAgent) {
      setFunnelData({});
      return;
    }
    let cancelled = false;
    setFunnelLoading(true);
    supabase
      .from("leads")
      .select("agent_status")
      .or(`routed_to_agent_id.eq.${selectedAgent.id},owner_agent_id.eq.${selectedAgent.id}`)
      .limit(5000)
      .then(({ data, error }) => {
        if (cancelled) return;
        setFunnelLoading(false);
        if (error) {
          console.error("[AdminAgents] Failed to fetch funnel data:", error);
          toast.error("Failed to load lead funnel data.");
          return;
        }
        const counts: Record<string, number> = {};
        for (const row of data || []) {
          const status = (!row.agent_status || row.agent_status === "pending") ? "new" : row.agent_status;
          counts[status] = (counts[status] || 0) + 1;
        }
        setFunnelData(counts);
      });
    return () => { cancelled = true; };
  }, [selectedAgent]);

  // Assign AM state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningAgent, setAssigningAgent] = useState<MockAgent | null>(null);
  const [amOptions, setAmOptions] = useState<AMOption[]>([]);
  const [selectedAmId, setSelectedAmId] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  // AM name lookup map for the table column: agentId → AM full_name
  const [amNameMap, setAmNameMap] = useState<Record<string, string>>({});

  // Fetch active AM assignments and build agent→AM name map
  const [amMapVersion, setAmMapVersion] = useState(0);
  useEffect(() => {
    const fetchAmAssignments = async () => {
      const { data: ams } = await supabase
        .from("account_managers")
        .select("id, full_name");

      if (!ams) return;

      const amNameById: Record<string, string> = {};
      ams.forEach((am) => { amNameById[am.id] = am.full_name; });

      const { data: assignments } = await supabase
        .from("client_am_assignments")
        .select("agent_id, am_id")
        .eq("status", "active");

      if (!assignments) return;

      const map: Record<string, string> = {};
      assignments.forEach((a) => {
        if (amNameById[a.am_id]) {
          map[a.agent_id] = amNameById[a.am_id];
        }
      });
      setAmNameMap(map);
    };
    fetchAmAssignments();
  }, [amMapVersion]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "Elite": return "bg-purple-100 text-purple-700 border-purple-200";
      case "Gold": return "bg-amber-100 text-amber-700 border-amber-200";
      case "Silver": return "bg-slate-100 text-slate-700 border-slate-200";
      default: return "bg-orange-100 text-orange-700 border-orange-200";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    return "text-destructive";
  };

  const isEditing = useMemo(() => !!formAgentId, [formAgentId]);

  // Pre-compute billing summary per agent
  const billingMap = useMemo(() => {
    const map = new Map<string, AgentBillingSummary>();
    for (const agent of clientAgents) {
      map.set(agent.id, computeAgentBilling(agent.id, contracts));
    }
    return map;
  }, [clientAgents, contracts]);

  const filteredAgents = useMemo(() => {
    if (!agentSearch.trim()) return clientAgents;
    const q = agentSearch.toLowerCase();
    return clientAgents.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.company || "").toLowerCase().includes(q)
    );
  }, [clientAgents, agentSearch]);

  const resetForm = () => {
    setFormAgentId(null);
    setFormName("");
    setFormEmail("");
    setFormCompany("");
    setFormVertical("life_insurance");
    setFormTier("Gold");
    setFormIsActive(true);
    setFormLicensedStates("");
    setFormLeadPrefs(buildDefaultPrefs("life_insurance"));
    setFormError(null);
    setShowForm(false);
  };

  const loadAgentToForm = (agent: MockAgent) => {
    setFormAgentId(agent.id);
    setFormName(agent.name ?? "");
    setFormEmail(agent.email ?? "");
    setFormCompany(agent.company ?? "");
    setFormVertical(agent.vertical);
    setFormTier(agent.tier);
    setFormIsActive(agent.isActive !== false);
    setFormLicensedStates(agent.licensedStates.join(", "));
    setFormLeadPrefs(parsePrefsFromAgent(agent));
    setFormError(null);
    setShowForm(true);
  };

  const handleVerticalChange = (newVertical: MockAgent["vertical"]) => {
    // Changing vertical resets all lead preferences — confirm if editing
    const hasCustomPrefs = formLeadPrefs.some((p) => p.isActive);
    if (hasCustomPrefs) {
      const confirmed = window.confirm(
        "Changing vertical will reset all lead preferences to defaults. Continue?"
      );
      if (!confirmed) return;
    }
    setFormVertical(newVertical);
    setFormLeadPrefs(buildDefaultPrefs(newVertical));
  };

  const updatePref = (leadTypeId: string, field: keyof LeadPrefState, value: unknown) => {
    setFormLeadPrefs((prev) =>
      prev.map((p) =>
        p.leadTypeId === leadTypeId ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSave = async () => {
    setFormError(null);

    // Validation
    const trimmedName = formName.trim();
    const trimmedEmail = formEmail.trim();

    if (!trimmedName) {
      setFormError("Name is required.");
      return;
    }
    if (!trimmedEmail) {
      setFormError("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    // Duplicate email check (exclude current agent when editing)
    const existingWithEmail = clientAgents.find(
      (a) => a.email?.toLowerCase() === trimmedEmail.toLowerCase() && a.id !== formAgentId
    );
    if (existingWithEmail) {
      setFormError(`Email already in use by "${existingWithEmail.name}".`);
      return;
    }

    // Cap validation
    const invalidCap = formLeadPrefs.find(
      (p) => p.isActive && p.dailyCap > p.monthlyCap
    );
    if (invalidCap) {
      setFormError(`"${invalidCap.leadTypeName}" has daily cap (${invalidCap.dailyCap}) greater than monthly cap (${invalidCap.monthlyCap}).`);
      return;
    }

    setFormSubmitting(true);
    try {
      const licensedStates = formLicensedStates
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);

      const leadPreferences = formLeadPrefs.map((p) => ({
        leadTypeId: p.leadTypeId,
        leadTypeName: p.leadTypeName,
        isActive: p.isActive,
        bidValue: p.bidValue ?? 35,
        dailyCap: p.dailyCap,
        monthlyCap: p.monthlyCap,
      }));

      const agent: MockAgent = {
        id: formAgentId ?? `agent-${Date.now()}`,
        name: trimmedName,
        email: trimmedEmail,
        company: formCompany.trim(),
        vertical: formVertical,
        tier: formTier,
        isActive: formIsActive,
        isDemo: false,
        isInternalUser: false,
        performanceScore: 0,
        walletBalance: 0,
        paidBalance: 0,
        promotionalBalance: 0,
        activeLeadTypesCount: leadPreferences.filter((p) => p.isActive).length,
        licensedStates,
        leadPreferences,
        performance: { avgResponseTime: 0, contactRate: 0, closeRate: 0 },
        leadsReceivedToday: 0,
        leadsReceivedThisMonth: 0,
        deliveryTarget: undefined,
      };

      await actions.upsertAgent(agent);
      toast.success(isEditing ? "Agent updated successfully." : "Agent created successfully.");
      resetForm();
    } catch (_err) {
      setFormError("Failed to save agent.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!formAgentId) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete "${formName}"? This action cannot be undone.`
    );
    if (!confirmed) return;
    setFormSubmitting(true);
    try {
      await actions.deleteAgent(formAgentId);
      toast.success("Agent deleted.");
      resetForm();
    } catch (_err) {
      toast.error("Failed to delete agent.");
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Assign AM ──────────────────────────────────────────────────

  const openAssignDialog = async (agent: MockAgent) => {
    setAssigningAgent(agent);
    setSelectedAmId("");
    setAssignNotes("");
    setAmOptions([]);
    setAssignDialogOpen(true);

    try {
      // Fetch active AMs with client counts
      const [amsRes, assignmentsRes] = await Promise.all([
        supabase
          .from("account_managers")
          .select("id, full_name, max_clients")
          .eq("is_active", true),
        supabase
          .from("client_am_assignments")
          .select("am_id")
          .eq("status", "active"),
      ]);

      if (amsRes.error) {
        toast.error("Failed to load account managers.");
        return;
      }

      const countMap: Record<string, number> = {};
      (assignmentsRes.data || []).forEach((a) => {
        countMap[a.am_id] = (countMap[a.am_id] || 0) + 1;
      });

      setAmOptions(
        (amsRes.data || []).map((am) => ({
          id: am.id,
          full_name: am.full_name,
          max_clients: am.max_clients ?? 50,
          active_client_count: countMap[am.id] || 0,
        }))
      );
    } catch (err) {
      console.error("[AdminAgents] Failed to load AM options:", err);
      toast.error("Failed to load account managers.");
    }
  };

  const handleAssignAM = async () => {
    if (!assigningAgent || !selectedAmId) return;
    setAssignSubmitting(true);

    try {
      // 1. End existing active assignment if any
      const { data: existing } = await supabase
        .from("client_am_assignments")
        .select("id")
        .eq("agent_id", assigningAgent.id)
        .eq("status", "active");

      if (existing && existing.length > 0) {
        const { error: transferError } = await supabase
          .from("client_am_assignments")
          .update({
            status: "transferred",
            ended_at: new Date().toISOString(),
            transfer_reason: "Reassigned by admin",
          })
          .eq("agent_id", assigningAgent.id)
          .eq("status", "active");

        if (transferError) {
          console.error("[AdminAgents] Failed to end existing assignment:", transferError);
          toast.error("Failed to end existing AM assignment. Please try again.");
          setAssignSubmitting(false);
          return;
        }
      }

      // 2. Insert new assignment
      const { error: insertError } = await supabase
        .from("client_am_assignments")
        .insert({
          agent_id: assigningAgent.id,
          am_id: selectedAmId,
          status: "active",
          assigned_by: user?.id || null,
          notes: assignNotes.trim() || null,
        });

      if (insertError) {
        console.error("[AdminAgents] Assignment insert failed:", insertError);
        toast.error("Failed to create AM assignment. Please try again.");
        setAssignSubmitting(false);
        return;
      }

      // 3. Update agents.assigned_am_id
      const { error: agentUpdateError } = await supabase
        .from("agents")
        .update({ assigned_am_id: selectedAmId })
        .eq("id", assigningAgent.id);

      if (agentUpdateError) {
        console.error("[AdminAgents] Failed to update agents.assigned_am_id:", agentUpdateError);
        // Assignment was created but agent record not synced — non-fatal, log warning
        toast.warning("AM assigned, but agent record sync failed. It will resolve on next page load.");
      }

      const amName = amOptions.find((am) => am.id === selectedAmId)?.full_name || "AM";
      toast.success(`${assigningAgent.name} assigned to ${amName}`);

      // Refresh AM name map from database
      setAmMapVersion((v) => v + 1);

      setAssignDialogOpen(false);
    } catch (err: any) {
      console.error("[AdminAgents] Assign AM error:", err);
      toast.error(err.message || "Failed to assign AM");
    } finally {
      setAssignSubmitting(false);
    }
  };

  // ── Helper: get AM name for an agent ──────────────────────────

  const getAgentAmName = (agent: MockAgent): string | null => {
    return amNameMap[agent.id] || null;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Agents</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {clientAgents.length} clients in the system
            </p>
          </div>
        </div>

        {/* Agent CRUD — Collapsible */}
        <Collapsible open={showForm} onOpenChange={setShowForm}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer select-none">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{isEditing ? `Edit Agent: ${formName || "—"}` : "Create Agent Manually"}</CardTitle>
                    <CardDescription>
                      {isEditing
                        ? "Update agent details and lead preferences"
                        : "Agents are normally created via payment link + signup. Use this for manual overrides only."}
                    </CardDescription>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${showForm ? "rotate-180" : ""}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name <span className="text-destructive">*</span></Label>
                    <Input value={formName} onChange={(e) => setFormName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email <span className="text-destructive">*</span></Label>
                    <Input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input value={formCompany} onChange={(e) => setFormCompany(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Vertical</Label>
                    <select
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={formVertical}
                      onChange={(e) => handleVerticalChange(e.target.value as MockAgent["vertical"])}
                    >
                      <option value="life_insurance">Life Insurance</option>
                      <option value="mortgage">Mortgage</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tier</Label>
                    <select
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={formTier}
                      onChange={(e) => setFormTier(e.target.value as MockAgent["tier"])}
                    >
                      <option value="Bronze">Bronze</option>
                      <option value="Silver">Silver</option>
                      <option value="Gold">Gold</option>
                      <option value="Elite">Elite</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="flex items-center gap-2">
                      <Switch checked={formIsActive} onCheckedChange={setFormIsActive} />
                      <span className="text-sm text-muted-foreground">
                        {formIsActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Licensed States (comma separated)</Label>
                    <Input
                      placeholder="TX, CA, FL"
                      value={formLicensedStates}
                      onChange={(e) => setFormLicensedStates(e.target.value)}
                    />
                  </div>
                </div>

                {/* Visual Lead Preferences Editor */}
                <div className="space-y-3">
                  <Label>Lead Preferences</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {formLeadPrefs.map((pref) => (
                      <div
                        key={pref.leadTypeId}
                        className={`rounded-lg border p-3 transition-colors ${
                          pref.isActive
                            ? "border-primary/30 bg-primary/5"
                            : "border-border bg-muted/30"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{pref.leadTypeName}</span>
                          <Switch
                            checked={pref.isActive}
                            onCheckedChange={(v) => updatePref(pref.leadTypeId, "isActive", v)}
                          />
                        </div>
                        {pref.isActive && (
                          <div className="flex gap-4 mt-3">
                            <div className="space-y-1 flex-1">
                              <Label className="text-xs text-muted-foreground">Daily Cap</Label>
                              <Input
                                type="number"
                                min={0}
                                value={pref.dailyCap}
                                onChange={(e) =>
                                  updatePref(pref.leadTypeId, "dailyCap", parseInt(e.target.value) || 0)
                                }
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1 flex-1">
                              <Label className="text-xs text-muted-foreground">Monthly Cap</Label>
                              <Input
                                type="number"
                                min={0}
                                value={pref.monthlyCap}
                                onChange={(e) =>
                                  updatePref(pref.leadTypeId, "monthlyCap", parseInt(e.target.value) || 0)
                                }
                                className="h-8"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {formError && <p className="text-sm text-destructive">{formError}</p>}
                <div className="flex items-center gap-2">
                  <Button onClick={handleSave} disabled={formSubmitting}>
                    <Save className="h-4 w-4 mr-2" />
                    {formSubmitting ? "Saving..." : "Save Agent"}
                  </Button>
                  <Button variant="outline" onClick={resetForm} disabled={formSubmitting}>
                    Cancel
                  </Button>
                  {isEditing && (
                    <Button variant="destructive" onClick={handleDelete} disabled={formSubmitting}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Agents Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Agent Directory</CardTitle>
                <CardDescription>
                  {filteredAgents.length === clientAgents.length
                    ? `${clientAgents.length} clients`
                    : `${filteredAgents.length} of ${clientAgents.length} clients`}
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Search by name, email, company"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Vertical</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead className="w-[160px]">Delivery</TableHead>
                  <TableHead>Account Manager</TableHead>
                  <TableHead className="text-right">Performance</TableHead>
                  <TableHead className="text-right">Active Types</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAgents.map((agent) => {
                  const amName = getAgentAmName(agent);
                  const billing = billingMap.get(agent.id);
                  const deliveryPct = billing && billing.totalCommitted > 0
                    ? Math.round((billing.totalDelivered / billing.totalCommitted) * 100)
                    : null;
                  return (
                    <TableRow key={agent.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-xs text-muted-foreground">{agent.company}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {agent.vertical.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getTierColor(agent.tier)} variant="outline">
                          {agent.tier}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={billing?.billingColor ?? ""} variant="outline">
                          {billing?.billingLabel ?? "—"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {deliveryPct !== null ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">
                                {billing!.totalDelivered}/{billing!.totalCommitted}
                              </span>
                              <span className="font-mono font-medium">{deliveryPct}%</span>
                            </div>
                            <Progress value={deliveryPct} className="h-1.5" />
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {amName ? (
                          <span className="text-sm text-foreground">{amName}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono font-medium ${getScoreColor(agent.performanceScore)}`}>
                          {agent.performanceScore}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {agent.activeLeadTypesCount}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedAgent(agent)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadAgentToForm(agent)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAssignDialog(agent)}
                            title="Assign Account Manager"
                          >
                            <Headphones className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/dashboard?viewAs=${agent.id}`)}
                            title="View as Client"
                          >
                            <Monitor className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Assign AM Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Account Manager</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-muted-foreground text-xs">Agent</Label>
              <p className="font-medium text-foreground">{assigningAgent?.name}</p>
              <p className="text-xs text-muted-foreground">{assigningAgent?.email}</p>
            </div>
            <div>
              <Label>Account Manager</Label>
              <Select value={selectedAmId} onValueChange={setSelectedAmId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an AM..." />
                </SelectTrigger>
                <SelectContent>
                  {amOptions.map((am) => (
                    <SelectItem key={am.id} value={am.id}>
                      {am.full_name} ({am.active_client_count}/{am.max_clients} clients)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder="Reason for assignment..."
                rows={2}
              />
            </div>
            <Button
              onClick={handleAssignAM}
              disabled={!selectedAmId || assignSubmitting}
              className="w-full"
            >
              {assignSubmitting ? "Assigning..." : "Assign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Detail Drawer */}
      <Sheet open={!!selectedAgent} onOpenChange={() => setSelectedAgent(null)}>
        <SheetContent className="w-[450px] sm:max-w-[450px] overflow-y-auto">
          {selectedAgent && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedAgent.name}</SheetTitle>
                <SheetDescription>{selectedAgent.email}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Company Info
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Company</p>
                      <p className="font-medium">{selectedAgent.company}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Vertical</p>
                      <p className="font-medium capitalize">
                        {selectedAgent.vertical.replace("_", " ")}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Tier</p>
                      <Badge className={getTierColor(selectedAgent.tier)} variant="outline">
                        {selectedAgent.tier}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <Badge variant={selectedAgent.isActive !== false ? "default" : "secondary"}>
                        {selectedAgent.isActive !== false ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Licensed States */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Licensed States
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedAgent.licensedStates.length > 0 ? (
                      selectedAgent.licensedStates.map(state => (
                        <Badge key={state} variant="secondary" className="font-mono">
                          {state}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No states configured</span>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Performance */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Performance Metrics
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Score</p>
                      <p className={`font-mono font-medium ${getScoreColor(selectedAgent.performanceScore)}`}>
                        {selectedAgent.performanceScore}/100
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Avg Response</p>
                      <p className="font-medium">{selectedAgent.performance.avgResponseTime}min</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Contact Rate</p>
                      <p className="font-medium">{selectedAgent.performance.contactRate}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Close Rate</p>
                      <p className="font-medium">{selectedAgent.performance.closeRate}%</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Delivery Stats */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Delivery Stats
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Today</p>
                      <p className="font-mono font-medium">{selectedAgent.leadsReceivedToday} leads</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">This Month</p>
                      <p className="font-mono font-medium">{selectedAgent.leadsReceivedThisMonth} leads</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Billing Summary */}
                {(() => {
                  const billing = billingMap.get(selectedAgent.id);
                  if (!billing) return null;
                  const agentContracts = contracts.filter(c => c.agentId === selectedAgent.id);
                  const activeContracts = agentContracts.filter(c => c.status === "active" || c.status === "at_risk");
                  return (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Billing Summary
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Status</p>
                          <Badge className={billing.billingColor} variant="outline">
                            {billing.billingLabel}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Active Contracts</p>
                          <p className="font-mono font-medium">{billing.activeContracts}</p>
                        </div>
                        {billing.pausedContracts > 0 && (
                          <div>
                            <p className="text-muted-foreground">Paused</p>
                            <p className="font-mono font-medium text-amber-600">{billing.pausedContracts}</p>
                          </div>
                        )}
                        {billing.cancellingContracts > 0 && (
                          <div>
                            <p className="text-muted-foreground">Cancelling</p>
                            <p className="font-mono font-medium text-red-600">{billing.cancellingContracts}</p>
                          </div>
                        )}
                        {billing.totalCommitted > 0 && (
                          <>
                            <div className="col-span-2">
                              <p className="text-muted-foreground mb-1">Delivery Progress</p>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span>{billing.totalDelivered} / {billing.totalCommitted} leads</span>
                                <span className="font-mono font-medium">
                                  {Math.round((billing.totalDelivered / billing.totalCommitted) * 100)}%
                                </span>
                              </div>
                              <Progress
                                value={Math.round((billing.totalDelivered / billing.totalCommitted) * 100)}
                                className="h-2"
                              />
                            </div>
                          </>
                        )}
                      </div>
                      {activeContracts.length > 0 && (
                        <div className="space-y-1.5 mt-2">
                          <p className="text-xs text-muted-foreground font-medium">Active Contracts</p>
                          {activeContracts.map(c => (
                            <div key={c.id} className="text-xs rounded border p-2 flex items-center justify-between">
                              <span className="truncate mr-2">{c.leadTypes?.join(", ") || "All types"}</span>
                              <span className="font-mono whitespace-nowrap">
                                {c.leadsDelivered}/{c.totalLeadsCommitted}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <Separator />

                {/* Conversion Funnel */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Conversion Funnel
                  </h4>
                  {funnelLoading ? (
                    <p className="text-xs text-muted-foreground">Loading...</p>
                  ) : (() => {
                    const totalLeads = Object.values(funnelData).reduce((s, n) => s + n, 0);
                    if (totalLeads === 0) {
                      return <p className="text-xs text-muted-foreground">No leads assigned to this agent yet.</p>;
                    }
                    const stages = [
                      { key: "new", label: "New", color: "bg-blue-500" },
                      { key: "contacted", label: "Contacted", color: "bg-amber-500" },
                      { key: "no_answer", label: "No Answer", color: "bg-orange-400" },
                      { key: "qualified", label: "Qualified", color: "bg-purple-500" },
                      { key: "proposal_sent", label: "Proposal Sent", color: "bg-indigo-500" },
                      { key: "won", label: "Won", color: "bg-green-500" },
                      { key: "lost", label: "Lost", color: "bg-red-500" },
                      { key: "not_qualified", label: "Not Qualified", color: "bg-gray-400" },
                    ];
                    return (
                      <div className="space-y-2">
                        {stages.map(({ key, label, color }) => {
                          const count = funnelData[key] || 0;
                          if (count === 0) return null;
                          const pct = Math.round((count / totalLeads) * 100);
                          return (
                            <div key={key} className="flex items-center gap-2 text-sm">
                              <span className="w-28 text-muted-foreground truncate">{label}</span>
                              <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${color}`}
                                  style={{ width: `${Math.max(pct, 2)}%` }}
                                />
                              </div>
                              <span className="w-16 text-right font-mono text-xs">
                                {count} ({pct}%)
                              </span>
                            </div>
                          );
                        })}
                        <div className="pt-1 text-xs text-muted-foreground">
                          {totalLeads} total leads
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <Separator />

                {/* Lead Preferences */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Lead Preferences
                  </h4>
                  <div className="space-y-2">
                    {selectedAgent.leadPreferences.length > 0 ? (
                      selectedAgent.leadPreferences.map(pref => (
                        <div
                          key={pref.leadTypeId}
                          className={`p-2 rounded border text-sm ${
                            pref.isActive
                              ? "bg-card border-border"
                              : "bg-muted/50 border-muted text-muted-foreground"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{pref.leadTypeName}</span>
                            <Badge variant={pref.isActive ? "default" : "secondary"} className="text-xs">
                              {pref.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          {pref.isActive && (
                            <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                              <span>Daily: {pref.dailyCap}</span>
                              <span>Monthly: {pref.monthlyCap}</span>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No preferences configured</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
